import React, { useMemo } from 'react';
import {
  type ButtonType,
  DataTableRenderCellParams,
  type ErpInputTableActions,
  ErpInputTableV2,
  GridEventListener
} from '@/components/custom/erp';
import type { SelectedCellInfo } from '@/hooks/use-input-table-rows';
import { formatDate, formatMoney } from '@/lib/formatUtils';
import type { GiaBan } from '@/types/schemas';

interface ItemPriceTabProps {
  mode: 'add' | 'edit' | 'view';
  rows: GiaBan[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: GridEventListener<'rowClick'>;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onEditRow: () => void;
  onPasteRow: () => void;
  onMoveRow: (direction: 'up' | 'down') => void;
}

const ItemPriceTab: React.FC<ItemPriceTabProps> = ({
  mode,
  rows,
  selectedRowUuid,
  onRowClick,
  onAddRow,
  onDeleteRow,
  onEditRow,
  onPasteRow,
  onMoveRow
}) => {
  const actions = useMemo<ErpInputTableActions>(
    () => ({
      add: onAddRow,
      edit: onEditRow,
      delete: onDeleteRow,
      copy: onEditRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      pin: () => {}
    }),
    [onAddRow, onDeleteRow, onEditRow, onMoveRow, onPasteRow]
  );

  const actionOrder = useMemo<ButtonType[]>(
    () => ['add', 'edit', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'pin'],
    []
  );

  return (
    <ErpInputTableV2
      autoHeight
      rows={rows}
      onRowClick={onRowClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={[
        {
          field: 'ngay_hl',
          headerName: 'Ngày hiệu lực',
          width: 200,
          renderCell: params => formatDate(params.row.ngay_hl)
        },
        {
          field: 'gia_ban',
          headerName: 'Giá bán',
          width: 200,
          renderCell: params => formatMoney(params.row.gia_nt2)
        },
        {
          field: 'ma_kh',
          headerName: 'Mã khách hàng',
          width: 200,
          renderCell: params => params.row.ma_kh_data?.customer_code
        }
      ]}
      getRowId={row => row?.uuid || ''}
      actions={actions}
      actionOrder={actionOrder}
      actionsDisabled={mode === 'view'}
      showIndexColumn
    />
  );
};

export default ItemPriceTab;
